"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { post } from "../utils/service";
import { Lock, Mail, EyeOff, Eye, X } from "lucide-react";
import { FaInstagram, FaXTwitter } from "react-icons/fa6";
import { PiTiktokLogoLight } from "react-icons/pi";
import { SlSocialFacebook } from "react-icons/sl";
import { PiYoutubeLogo } from "react-icons/pi";
import { assets } from "../assets/assets";

const ForgotPasswordModal = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const response = await post("auth/forgot-password", { email });
      if (response.success) {
        toast.success(response.message || "Reset code sent to your email");
        setStep(2);
      } else {
        toast.error(response.message || "Failed to send reset code");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!otp || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await post("auth/reset-password", {
        email,
        otp,
        newPassword,
        confirmPassword,
      });
      if (response.success) {
        toast.success(response.message || "Password reset successfully!");
        onComplete();
        onClose();
        setStep(1);
        setEmail("");
        setOtp("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        toast.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmail("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-labelledby="forgot-password-title"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />
      <div className="relative z-50 w-full max-w-md max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-lg rounded-lg shadow-lg border border-white/20">
        <div className="flex items-center justify-between p-6 border-b border-white/20">
          <h2 id="forgot-password-title" className="text-xl font-semibold">
            {step === 1 ? "Reset Password" : "Enter Reset Code"}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-secondary" />
            </div>
            <p className="text-xs text-gray-600">
              {step === 1
                ? "Enter your email to receive a reset code"
                : "Enter the code sent to your email and your new password"}
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all"
                  placeholder="Enter your email"
                  disabled={loading}
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 bg-gray-200 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-300 transition text-xs"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary text-secondary font-medium py-3 rounded-lg transition disabled:opacity-50 text-xs"
                >
                  {loading ? "Sending..." : "Send Code"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="w-full px-3 py-3 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs text-center tracking-widest transition-all"
                  placeholder="Enter 4-digit code"
                  maxLength="4"
                  disabled={loading}
                  required
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all"
                  placeholder="New Password"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all"
                  placeholder="Confirm New Password"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 bg-gray-200 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-300 transition text-xs"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary text-secondary font-medium py-3 rounded-lg transition disabled:opacity-50 text-xs"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const validateEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const validatePassword = (value) => {
    return value.length >= 6;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    let valid = true;
  
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }
  
    if (!validatePassword(password)) {
      setPasswordError("Password must be at least 6 characters.");
      valid = false;
    } else {
      setPasswordError("");
    }
  
    setSubmitted(true);
  
    if (valid) {
      setLoading(true);
      
      try {
        const response = await post('auth/login', { email, password });
        
        if (response.success) {
          const { token, user } = response.data;
          
          localStorage.setItem('jwt', token);
          localStorage.setItem('userId', user.id.toString());
          localStorage.setItem('email', user.email);
          localStorage.setItem('role', user.role);
          localStorage.setItem('isLoggedIn', 'true');
          
          toast.success(response.message || 'Login successful!');
          navigate('/');
          
          setEmail("");
          setPassword("");
          setSubmitted(false);
        } else {
          toast.error(response.message || 'Login failed. Please check your credentials.');
        }
      } catch (error) {
        console.error('Login error:', error);
        
        if (error.response?.data) {
          const errorMessage = error.response.data.message || 'Invalid credentials';
          toast.error(errorMessage);
        } else if (error.request) {
          toast.error('No response from server. Please check your connection.');
        } else {
          toast.error('An error occurred while logging in. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <style jsx>{`
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="min-h-screen w-full hidden xl:flex items-center justify-center bg-gray-100">
        <div className="h-screen w-screen flex overflow-hidden bg-white">
          <div className="w-1/2 relative overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${assets.banner})` }}
            />

            <div className="absolute inset-0 bg-black/40"></div>
          </div>

          <div className="w-1/2 bg-white flex flex-col h-full">
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-sm px-8">
                <div className="flex-shrink-0 mb-8">
                  <div className="flex justify-center mb-6">
                    <img
                      src={assets.MainLogo}
                      alt="Social Gems"
                      className="h-12 w-fit object-contain"
                    />
                  </div>

                  <div className="text-center">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                      Welcome Back
                    </h1>
                    <p className="text-gray-600 text-xs">
                      Sign in to your account
                    </p>
                  </div>
                </div>

                <form
                  className="flex flex-col gap-5"
                  onSubmit={handleLogin}
                  noValidate
                >
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-medium text-gray-700 mb-2"
                    >
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      placeholder="admin@chiedzacheafrica.com"
                      className={`w-full px-4 py-3 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all ${
                        emailError ? "border-red-500" : ""
                      }`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-invalid={!!emailError}
                      aria-describedby="email-error"
                      disabled={loading}
                    />
                    {emailError && (
                      <p id="email-error" className="text-red-500 text-xs mt-1">
                        {emailError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-xs font-medium text-gray-700 mb-2"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        placeholder="Enter your password"
                        className={`w-full pr-10 py-3 px-4 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all ${
                          passwordError ? "border-red-500" : ""
                        }`}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        aria-invalid={!!passwordError}
                        aria-describedby="password-error"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {passwordError && (
                      <p
                        id="password-error"
                        className="text-red-500 text-xs mt-1"
                      >
                        {passwordError}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="text-xs text-secondary hover:underline font-medium"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary text-secondary font-medium py-3 rounded-lg transition disabled:opacity-50 text-xs hover:bg-primary"
                  >
                    {loading ? "Signing in..." : "Sign in"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-screen w-full lg:flex hidden xl:hidden bg-gray-50">
        <div className="w-full h-screen flex shadow-xl">
          <div className="w-1/2 relative overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${assets.banner})` }}
            />

            <div className="absolute inset-0 bg-black/20"></div>
          </div>

          <div className="w-1/2 bg-white flex flex-col h-full">
            <div className="flex-shrink-0 p-6 pb-4">
              <div className="flex justify-center mb-4">
                <img
                  src={assets.LogoIcon}
                  alt="Social Gems"
                  className="h-10 w-10 object-contain"
                />
              </div>

              <div className="text-center">
                <h1 className="text-xl font-semibold text-gray-900 mb-2">
                  Welcome Back
                </h1>
                <p className="text-gray-600 text-xs">Sign in to your account</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 scrollbar-none">
              <form
                className="flex flex-col gap-4"
                onSubmit={handleLogin}
                noValidate
              >
                <div>
                  <label
                    htmlFor="email-tablet"
                    className="block text-xs font-medium text-gray-700 mb-2"
                  >
                    Email address
                  </label>
                  <input
                    type="email"
                    id="email-tablet"
                    placeholder="admin@chiedzacheafrica.com"
                    className={`w-full px-4 py-3 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all ${
                      emailError ? "border-red-500" : ""
                    }`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!emailError}
                    aria-describedby="email-error-tablet"
                    disabled={loading}
                  />
                  {emailError && (
                    <p
                      id="email-error-tablet"
                      className="text-red-500 text-xs mt-1"
                    >
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password-tablet"
                    className="block text-xs font-medium text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password-tablet"
                      placeholder="Enter your password"
                      className={`w-full pr-10 py-3 px-4 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all ${
                        passwordError ? "border-red-500" : ""
                      }`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-invalid={!!passwordError}
                      aria-describedby="password-error-tablet"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <p
                      id="password-error-tablet"
                      className="text-red-500 text-xs mt-1"
                    >
                      {passwordError}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline font-medium"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-secondary font-medium py-3 rounded-lg transition disabled:opacity-50 text-xs hover:bg-primary"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-screen w-full flex flex-col lg:hidden bg-gray-50">
        <div className="w-full h-screen flex flex-col">
          <div className="flex-shrink-0 bg-primary py-8 px-4 text-center">
            <img
              src={assets.MainLogo}
              alt="Social Gems Logo"
              className="h-16 w-auto object-contain mx-auto"
            />
          </div>

          <div className="flex-1 bg-white overflow-hidden flex flex-col">
            <div className="flex-shrink-0 p-6 pb-4">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Welcome Back
                </h2>
                <p className="text-gray-600 text-xs">Sign in to your account</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-6 scrollbar-none">
              <form
                className="flex flex-col gap-5 mb-8"
                onSubmit={handleLogin}
                noValidate
              >
                <div>
                  <label
                    htmlFor="email-mobile"
                    className="block text-xs font-medium text-gray-700 mb-2"
                  >
                    Email address
                  </label>
                  <input
                    type="email"
                    id="email-mobile"
                    placeholder="admin@chiedzacheafrica.com"
                    className={`w-full px-4 py-3 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all ${
                      emailError ? "border-red-500" : ""
                    }`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!emailError}
                    aria-describedby="email-error-mobile"
                    disabled={loading}
                  />
                  {emailError && (
                    <p
                      id="email-error-mobile"
                      className="text-red-500 text-xs mt-1"
                    >
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="password-mobile"
                    className="block text-xs font-medium text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password-mobile"
                      placeholder="Enter your password"
                      className={`w-full pr-10 py-3 px-4 rounded-lg border-0 outline-0 bg-gray-200 focus:bg-white focus:border focus:border-primary text-black text-xs transition-all ${
                        passwordError ? "border-red-500" : ""
                      }`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-invalid={!!passwordError}
                      aria-describedby="password-error-mobile"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {passwordError && (
                    <p
                      id="password-error-mobile"
                      className="text-red-500 text-xs mt-1"
                    >
                      {passwordError}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline font-medium"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-secondary font-medium py-3 rounded-lg transition disabled:opacity-50 text-xs"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </button>
              </form>

            </div>
          </div>
        </div>
      </div>
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onComplete={() => {
          toast.success("You can now sign in with your new password");
        }}
      />
    </>
  );
};
export default Login;
